import React from "react";
import { AdminLayout } from "../shared/layout";
import { useQuery } from "@tanstack/react-query";
import { getProductAnalytics } from "@/data";
import { StatCard } from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users,
  MessageCircle,
  CheckCircle2,
  Repeat,
  ShieldAlert,
  BookOpen,
  Sparkles,
} from "lucide-react";

function pct(part, whole) {
  if (!whole) return "0%";
  return `${Math.round((part / whole) * 100)}%`;
}

export function AdminProductScreen() {
  const { data, isLoading } = useQuery({
    queryKey: ["product-analytics"],
    queryFn: getProductAnalytics,
  });
  const d = data?.data;
  const funnel = d?.funnel || {};
  const usage = d?.usage || {};
  const topScenarios = d?.topScenarios || [];
  const sessionsDaily = d?.sessionsDaily || [];
  const maxDaily = Math.max(1, ...sessionsDaily.map((x) => x.count));

  return (
    <AdminLayout title="Product analytics" loading={isLoading}>
      <div className="space-y-6">
        {/* Activation funnel */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Activation funnel</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Total users" value={funnel.totalUsers ?? "—"} icon={Users} />
            <StatCard
              title="Tried practice"
              value={funnel.usersWithSession ?? "—"}
              icon={MessageCircle}
              description={pct(funnel.usersWithSession, funnel.totalUsers) + " of users"}
            />
            <StatCard
              title="Completed a session"
              value={funnel.usersWithCompleted ?? "—"}
              icon={CheckCircle2}
              description={pct(funnel.usersWithCompleted, funnel.totalUsers) + " of users"}
            />
            <StatCard
              title="Returning (2+ days)"
              value={funnel.returningUsers ?? "—"}
              icon={Repeat}
              description={pct(funnel.returningUsers, funnel.totalUsers) + " of users"}
            />
          </div>
        </div>

        {/* Feature usage */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Feature usage</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Practice sessions"
              value={usage.totalSessions ?? "—"}
              icon={MessageCircle}
              description={`${usage.completedSessions ?? 0} completed`}
            />
            <StatCard
              title="Freeform vs scenario"
              value={`${usage.freeformSessions ?? 0} / ${usage.scenarioSessions ?? 0}`}
              icon={Sparkles}
              description="open or own / scenario"
            />
            <StatCard title="Red flag checks" value={usage.flagAnalyses ?? "—"} icon={ShieldAlert} />
            <StatCard title="Journal entries" value={usage.journalEntries ?? "—"} icon={BookOpen} />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Quality */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quality</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-primary">
                  {d?.avgScore != null ? d.avgScore : "—"}
                </span>
                <span className="text-muted-foreground">/ 10 avg coaching score</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Average across all completed sessions. Trending up means people are improving.
              </p>
            </CardContent>
          </Card>

          {/* Top scenarios */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Most-used scenarios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {topScenarios.length === 0 && (
                <p className="text-sm text-muted-foreground">No data yet.</p>
              )}
              {topScenarios.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="truncate">{s.title}</span>
                  <span className="font-semibold tabular-nums">{s.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Sessions per day */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sessions — last 14 days</CardTitle>
          </CardHeader>
          <CardContent>
            {sessionsDaily.length === 0 ? (
              <p className="text-sm text-muted-foreground">No sessions yet.</p>
            ) : (
              <div className="flex items-end gap-1 h-32">
                {sessionsDaily.map((x, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className="w-full rounded-t bg-primary/70"
                      style={{ height: `${(x.count / maxDaily) * 100}%` }}
                      title={`${x.date}: ${x.count}`}
                    />
                    <span className="text-[9px] text-muted-foreground">{x.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
